import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MagnifyingGlass, Cube, Pulse, ArrowsLeftRight, FilePlus, Fire, TrendUp, Lightning, Sparkle,
  FunnelSimple, Copy, Star, CaretLeft, CaretRight, X,
} from "@phosphor-icons/react";
import { API, http, getWithRetry } from "@/lib/constants";
import { cn, formatBigCount, formatRelativeTime } from "@/lib/format";
import { useAnalytics } from "@/components/assets/useAssets";
import {
  WindowTabs, Delta, MiniSpark, TokenLogo, VerifiedTick, Cip68Badge, cip68Label,
  assetLabel, fmtInt, fmtQty, fmtQtyMeta, windowDelta, trimPartial, VsPrevious,
} from "@/components/assets/parts";
import { TokenMetaProvider, useTokenMeta, useRequestMeta } from "@/components/assets/tokenMeta";
import { useWatchlist } from "@/components/assets/watchlist";

const N_FOR = { "24h": 1, "7d": 7, "30d": null };
const HEX = /^[0-9a-f]+$/i;

const typeOf = (item) => {
  const lbl = cip68Label(item?.asset_name);
  if (lbl?.kind === "NFT") return "NFT";
  if (lbl?.kind === "FT" || lbl?.kind === "RFT") return "TOKEN";
  return null;
};

/* ----------------------------- metric cards ----------------------------- */
const StatCard = ({ icon: Icon, label, value, footer, tone }) => (
  <div className="relative overflow-hidden rounded-xl border border-primary/15 bg-card/40 backdrop-blur-md px-4 py-3">
    <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.18em] text-muted-foreground mono">
      <Icon size={13} weight="duotone" className={tone === "red" ? "text-rose-400/70" : "text-primary/70"} /> {label}
    </div>
    <div className={cn("mt-1.5 font-heading font-bold tabnum text-[22px] leading-none", tone === "red" ? "text-rose-300" : "text-[#22d3ee] text-glow-cyan")}>{value}</div>
    <div className="mt-1.5 min-h-[16px]">{footer}</div>
  </div>
);

const StatCards = ({ stats, daily, win }) => {
  const n = N_FOR[win];
  const txD = windowDelta(daily, "tx_count", n);
  const mintD = windowDelta(daily, "mint_events", n);
  const burnD = windowDelta(daily, "burn_events", n);
  const total = stats?.total_assets;
  const totalPct = total?.baseline ? (total.added_since_baseline / total.baseline) * 100 : null;
  const txTotal = txD ? txD.cur : (daily || []).slice(-n || -1).reduce((s, p) => s + (p.tx_count || 0), 0);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      <StatCard icon={Cube} label="Total assets" value={total ? formatBigCount(total.value) : "—"} footer={<VsPrevious label="since baseline" pct={totalPct} delta={total?.added_since_baseline} />} />
      <StatCard icon={Pulse} label={`Active assets (${win})`} value={stats?.active_assets?.[win] != null ? fmtInt(stats.active_assets[win]) : "—"} footer={<span className="text-[10px] mono text-muted-foreground/60">live on-chain</span>} />
      <StatCard icon={ArrowsLeftRight} label={`Transactions (${win})`} value={txD ? formatBigCount(txTotal) : "—"} footer={txD ? <VsPrevious label="vs previous" pct={txD.pct} delta={txD.delta} /> : <span className="text-[10px] mono text-muted-foreground/60">—</span>} />
      <StatCard icon={FilePlus} label={`Mint events (${win})`} value={stats?.mint_burn?.[win] ? fmtInt(stats.mint_burn[win].mint_events) : "—"} footer={mintD ? <VsPrevious label="vs previous" pct={mintD.pct} delta={mintD.delta} /> : <span className="text-[10px] mono text-muted-foreground/60">—</span>} />
      <StatCard icon={Fire} tone="red" label={`Burn events (${win})`} value={stats?.mint_burn?.[win] ? fmtInt(stats.mint_burn[win].burn_events) : "—"} footer={burnD ? <VsPrevious label="vs previous" pct={burnD.pct} delta={burnD.delta} /> : <span className="text-[10px] mono text-muted-foreground/60">—</span>} />
    </div>
  );
};

/* ------------------------------- sidebar -------------------------------- */
const Radio = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={cn("w-full text-left px-2.5 py-1.5 rounded-md text-[12px] mono transition-colors", active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/20")}>{children}</button>
);
const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70 mono">{title}</div>
    {children}
  </div>
);

const Filters = ({ f, set, reset }) => (
  <aside data-testid="explorer-filters" className="w-full lg:w-[232px] shrink-0 space-y-5 rounded-2xl border border-primary/15 bg-card/30 backdrop-blur-md p-4 self-start">
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.2em] text-foreground mono inline-flex items-center gap-2"><FunnelSimple size={14} className="text-primary" /> Filters</span>
      <button type="button" data-testid="explorer-reset" onClick={reset} className="text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-primary">Reset</button>
    </div>
    <Section title="Asset type">
      <div className="flex gap-1.5">
        {["all", "tokens", "nfts"].map((t) => (
          <button key={t} type="button" data-testid={`filter-type-${t}`} onClick={() => set({ type: t })} className={cn("flex-1 px-2 py-1.5 rounded-md text-[11px] uppercase tracking-[0.1em] mono border transition-colors", f.type === t ? "bg-primary/15 border-primary/40 text-primary" : "border-primary/15 text-muted-foreground hover:text-foreground")}>{t === "all" ? "All" : t === "tokens" ? "Tokens" : "NFTs"}</button>
        ))}
      </div>
    </Section>
    <Section title="Policy ID">
      <div className="flex items-center gap-1.5 rounded-md border border-primary/15 bg-black/30 px-2.5">
        <input data-testid="filter-policy" value={f.policy} onChange={(e) => set({ policy: e.target.value.trim() })} placeholder="policy id (hex)" spellCheck={false} className="flex-1 min-w-0 bg-transparent outline-none py-2 text-[11px] mono text-foreground placeholder:text-muted-foreground/40" />
        {f.policy ? <button type="button" onClick={() => set({ policy: "" })} className="text-muted-foreground hover:text-foreground"><X size={12} /></button> : null}
      </div>
    </Section>
    <Section title="Search">
      <div className="flex items-center gap-1.5 rounded-md border border-primary/15 bg-black/30 px-2.5">
        <MagnifyingGlass size={13} className="text-primary/60 shrink-0" />
        <input data-testid="filter-search-side" value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="name, policy or fingerprint" spellCheck={false} className="flex-1 min-w-0 bg-transparent outline-none py-2 text-[11px] mono text-foreground placeholder:text-muted-foreground/40" />
      </div>
    </Section>
    <Section title="Newly minted">
      {[["any", "Any time"], ["24h", "Last 24 hours"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]].map(([k, lbl]) => (
        <Radio key={k} active={f.minted === k} onClick={() => set({ minted: k })} testid={`filter-minted-${k}`}>{lbl}</Radio>
      ))}
    </Section>
    <Section title="Results">
      <select data-testid="filter-results" value={f.count} onChange={(e) => set({ count: Number(e.target.value) })} className="w-full rounded-md border border-primary/15 bg-black/30 px-2.5 py-2 text-[12px] mono text-foreground outline-none">
        {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
      </select>
    </Section>
  </aside>
);

/* ---------------------------- detail panel ------------------------------ */
const summaryCache = new Map();
const fetchSummary = (asset) => {
  if (!summaryCache.has(asset)) summaryCache.set(asset, getWithRetry(`${API}/assets/${asset}`, { timeout: 12000 }, { retries: 1 }).then((r) => r.data).catch(() => null));
  return summaryCache.get(asset);
};

const Ident = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2 py-1.5">
    <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70 mono">{label}</span>
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className="truncate text-[12px] mono text-foreground max-w-[150px]">{value}</span>
      <button type="button" onClick={() => navigator.clipboard?.writeText(value)} className="text-muted-foreground hover:text-primary shrink-0"><Copy size={12} /></button>
    </span>
  </div>
);

const DetailPanel = ({ item, onPickAsset }) => {
  const { meta } = useTokenMeta();
  const { list, toggle } = useWatchlist();
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); if (item?.asset) { let m = true; fetchSummary(item.asset).then((d) => { if (m) setS(d); }); return () => { m = false; }; } }, [item?.asset]);
  if (!item) return (
    <aside data-testid="explorer-detail" className="w-full xl:w-[320px] shrink-0 rounded-2xl border border-primary/15 bg-card/30 backdrop-blur-md p-6 self-start text-center text-[12px] text-muted-foreground mono">Select an asset to see its details.</aside>
  );
  const reg = meta.get(item.asset);
  const { text } = assetLabel(item);
  const name = reg?.name || reg?.ticker || text;
  const type = typeOf(item);
  const pinned = list.some((x) => x.asset === item.asset);
  const dec = Number(reg?.decimals) || 0;
  return (
    <aside data-testid="explorer-detail" className="w-full xl:w-[320px] shrink-0 rounded-2xl border border-primary/20 bg-card/40 backdrop-blur-md self-start box-glow overflow-hidden">
      <div className="p-4 border-b border-primary/10">
        <div className="flex items-start gap-3">
          <TokenLogo item={item} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate font-heading text-lg text-foreground">{name}</span>
              {reg?.name ? <VerifiedTick /> : null}
              <button type="button" data-testid="detail-star" onClick={() => toggle(item)} className={cn("ml-auto shrink-0 transition-colors", pinned ? "text-[#22d3ee]" : "text-muted-foreground hover:text-[#22d3ee]")}><Star size={16} weight={pinned ? "fill" : "regular"} /></button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.12em] mono text-muted-foreground/70 mt-0.5">{type || "Native asset"} · {item.policy_id ? `${item.policy_id.slice(0, 6)}…${item.policy_id.slice(-4)}` : ""}</div>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-primary/70 mono mb-1">Asset identifiers</div>
          <Ident label="Asset name" value={name} />
          <Ident label="Policy ID" value={item.policy_id || "—"} />
          <Ident label="Fingerprint" value={item.fingerprint || "—"} />
        </div>
        <div className="border-t border-primary/10 pt-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-primary/70 mono mb-2">Quantities</div>
          <div className="text-[10px] mono text-muted-foreground/70">Raw on-chain quantity</div>
          <div className="text-[20px] font-heading tabnum text-foreground">{s ? fmtQty(s.quantity) : "…"}</div>
          <div className="mt-2 text-[10px] mono text-muted-foreground/70">Decimal-adjusted quantity</div>
          <div className="text-[18px] font-heading tabnum text-[#22d3ee]">{s ? fmtQtyMeta(s.quantity, { decimals: dec }) : "…"} <span className="text-[10px] text-muted-foreground/60">(decimals: {dec})</span></div>
        </div>
        <div className="border-t border-primary/10 pt-3 flex items-center justify-between">
          <span className="text-[11px] mono text-muted-foreground">Mint / burn events</span>
          <span className="text-[13px] mono tabnum text-foreground">{s ? fmtInt(s.mint_or_burn_count) : "…"}</span>
        </div>
        <button type="button" data-testid="detail-open" onClick={() => onPickAsset(item.asset)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/10 hover:bg-primary/20 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] mono text-primary transition-colors">Open full details <CaretRight size={13} /></button>
      </div>
    </aside>
  );
};

/* -------------------------------- table --------------------------------- */
const TABS = [
  { key: "trending", label: "Trending", icon: TrendUp },
  { key: "active", label: "Most active", icon: Lightning },
  { key: "new", label: "New assets", icon: Sparkle },
];

const Row = ({ it, i, rank, selected, onSelect }) => {
  const { meta } = useTokenMeta();
  const reg = meta.get(it.asset);
  const { text } = assetLabel(it);
  const name = reg?.name || reg?.ticker || text;
  const type = typeOf(it);
  const up = (it.tx_count || 0) >= (it.previous_tx_count || 0);
  const hasPrev = it.previous_tx_count != null && it.previous_tx_count > 0;
  return (
    <tr data-testid={`explorer-row-${i}`} onClick={() => onSelect(it)} className={cn("border-b border-primary/5 cursor-pointer transition-colors", selected ? "bg-primary/10" : "hover:bg-primary/5")}>
      <td className="px-3 py-2.5 text-right text-muted-foreground text-[12px]">{rank}</td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0 max-w-[260px]">
          <TokenLogo item={it} size={28} testid={`explorer-logo-${i}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              <span className="truncate text-[13px] mono text-foreground">{name}</span>
              {reg?.name ? <VerifiedTick /> : null}
            </div>
            <div className="text-[10px] mono text-muted-foreground/60 truncate">{it.fingerprint ? `${it.fingerprint.slice(0, 14)}…` : ""}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">{type ? <span className={cn("text-[9px] uppercase tracking-[0.1em] mono px-1.5 py-0.5 rounded border", type === "NFT" ? "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10" : "text-primary/80 border-primary/25 bg-primary/10")}>{type}</span> : <Cip68Badge assetName={it.asset_name} />}</td>
      <td className="px-3 py-2.5 text-right mono tabnum text-foreground/90">{it.tx_count != null ? fmtInt(it.tx_count) : "—"}{hasPrev ? <span className="text-muted-foreground/50 text-[11px]"> ({fmtInt(it.previous_tx_count)})</span> : null}</td>
      <td className="px-3 py-2.5 text-right">{it.delta_pct != null || hasPrev ? <Delta pct={it.delta_pct} testid={`explorer-delta-${i}`} /> : <span className="text-muted-foreground/40">—</span>}</td>
      <td className="px-3 py-2.5 text-right">{hasPrev ? <span className="inline-flex justify-end w-full"><MiniSpark values={[it.previous_tx_count, it.tx_count]} color={up ? "#34d399" : "#fb7185"} width={56} height={20} /></span> : <span className="text-muted-foreground/40 text-[11px]">—</span>}</td>
    </tr>
  );
};

const ExplorerInner = ({ onPickAsset }) => {
  const [win, setWin] = useState("24h");
  const [f, setF] = useState({ type: "all", policy: "", q: "", minted: "any", count: 10 });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const set = (patch) => { setF((p) => ({ ...p, ...patch })); setPage(1); };
  const reset = () => { setF({ type: "all", policy: "", q: "", minted: "any", count: 10 }); setPage(1); };

  const { data: stats } = useAnalytics("/assets/stats", {}, { ttl: 60000, refreshMs: 90000 });
  const { data: chart } = useAnalytics("/assets/charts", { days: "30" }, { ttl: 300000, refreshMs: 120000 });
  const daily = trimPartial(chart?.points || []);

  const policyApplied = /^[0-9a-f]{56}$/i.test(f.policy);
  const [tabKey, setTabKey] = useState("trending");
  const activeSource = policyApplied ? "policy" : f.minted !== "any" ? "new" : tabKey;

  const endpoint = activeSource === "policy" ? `/assets/policy/${f.policy}`
    : activeSource === "new" ? "/assets/recent-mints"
    : activeSource === "active" ? "/assets/activity"
    : "/assets/trending";
  const params = activeSource === "policy" || activeSource === "new"
    ? { count: f.count, page }
    : { window: win, count: f.count, page };
  const { data, loading, error, reload } = useAnalytics(endpoint, params, { ttl: 60000 });
  // The policy endpoint returns a plain array of {asset, quantity}; everything
  // else returns {items}. Normalise + derive policy/name from the asset unit.
  const rawItems = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((x) => ({ asset: x.asset, quantity: x.quantity, policy_id: x.asset?.slice(0, 56), asset_name: x.asset?.slice(56) }));
    }
    return data?.items || [];
  }, [data]);

  const MINTED_SECONDS = { "24h": 86400, "7d": 604800, "30d": 2592000 };
  const items = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const seen = new Set();
    const cutoff = f.minted !== "any" && MINTED_SECONDS[f.minted] ? Math.floor(Date.now() / 1000) - MINTED_SECONDS[f.minted] : null;
    return rawItems.filter((it) => {
      if (!it.asset || seen.has(it.asset)) return false; // dedupe (recent-mints repeats)
      seen.add(it.asset);
      if (cutoff && activeSource === "new" && it.block_time && Number(it.block_time) < cutoff) return false;
      if (f.type !== "all") {
        const t = typeOf(it);
        if (f.type === "nfts" && t !== "NFT") return false;
        if (f.type === "tokens" && t === "NFT") return false;
      }
      if (q) {
        const name = assetLabel(it).text.toLowerCase();
        const hay = `${name} ${it.fingerprint || ""} ${it.policy_id || ""} ${it.asset || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rawItems, f.q, f.type, f.minted, activeSource]);

  useRequestMeta(items.map((it) => it.asset));

  // auto-select first row when list changes and nothing valid selected
  useEffect(() => {
    if (!items.length) { setSelected(null); return; }
    if (!selected || !items.some((it) => it.asset === selected.asset)) setSelected(items[0]);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const showActivityCols = activeSource === "trending";

  return (
    <main data-testid="asset-explorer-page" className="max-w-[1480px] mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mono mb-1 inline-flex items-center gap-2"><MagnifyingGlass size={13} /> Asset Explorer</div>
          <h1 data-testid="explorer-title" className="font-heading text-3xl lg:text-[40px] font-bold tracking-tight text-foreground leading-none">Asset Explorer</h1>
          <p className="text-[12px] text-muted-foreground mt-1">Find what is active on-chain.</p>
        </div>
        <WindowTabs value={win} onChange={setWin} testid="explorer-window" />
      </header>

      <StatCards stats={stats} daily={daily} win={win} />

      <div className="flex flex-col lg:flex-row gap-5 items-start">
        <Filters f={f} set={set} reset={reset} />

        <section className="flex-1 min-w-0 rounded-2xl border border-primary/15 bg-card/30 backdrop-blur-md overflow-hidden">
          <div className="p-3 flex items-center gap-3 flex-wrap border-b border-primary/10">
            <div className="flex items-center gap-1.5">
              {TABS.map((t) => (
                <button key={t.key} type="button" data-testid={`explorer-tab-${t.key}`} disabled={policyApplied || f.minted !== "any"} onClick={() => setTabKey(t.key)} className={cn("inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] uppercase tracking-[0.1em] mono border transition-colors disabled:opacity-40", (!policyApplied && f.minted === "any" && tabKey === t.key) ? "bg-primary/15 border-primary/40 text-primary glow-active" : "border-transparent text-muted-foreground hover:text-foreground")}>
                  <t.icon size={13} /> {t.label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1.5 rounded-lg border border-primary/15 bg-black/30 px-2.5 min-w-[200px]">
              <MagnifyingGlass size={13} className="text-primary/60" />
              <input data-testid="explorer-search" value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search results…" spellCheck={false} className="flex-1 min-w-0 bg-transparent outline-none py-2 text-[12px] mono text-foreground placeholder:text-muted-foreground/40" />
            </div>
          </div>

          {policyApplied ? <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.12em] mono text-primary/70 bg-primary/5">assets under policy {f.policy.slice(0, 12)}…</div> : null}

          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[620px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-3 py-2.5 text-right font-sans font-medium border-b border-primary/10 w-8">#</th>
                  <th className="px-3 py-2.5 text-left font-sans font-medium border-b border-primary/10">Asset</th>
                  <th className="px-3 py-2.5 text-left font-sans font-medium border-b border-primary/10">Type</th>
                  <th className="px-3 py-2.5 text-right font-sans font-medium border-b border-primary/10">Transactions {showActivityCols ? `${win} (prev)` : ""}</th>
                  <th className="px-3 py-2.5 text-right font-sans font-medium border-b border-primary/10">Vs previous</th>
                  <th className="px-3 py-2.5 text-right font-sans font-medium border-b border-primary/10">Activity</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => <Row key={`${it.asset}-${it.ident ?? i}`} it={it} i={i} rank={(page - 1) * f.count + i + 1} selected={selected?.asset === it.asset} onSelect={setSelected} />)}
              </tbody>
            </table>
          </div>

          {loading && !rawItems.length ? <div className="px-4 py-10 text-center text-[12px] text-muted-foreground mono animate-pulse">loading assets…</div> : null}
          {error && !rawItems.length ? <div className="px-4 py-10 text-center text-[12px] text-rose-300 mono">The data provider is busy. <button onClick={reload} className="underline">Retry</button></div> : null}
          {!loading && !error && !items.length ? <div data-testid="explorer-empty" className="px-4 py-10 text-center text-[12px] text-muted-foreground mono">No assets match these filters.</div> : null}

          <div className="p-3 flex items-center justify-between border-t border-primary/10">
            <span className="text-[10px] mono text-muted-foreground/60">Showing {items.length ? `${(page - 1) * f.count + 1}–${(page - 1) * f.count + items.length}` : 0} {f.q || f.type !== "all" ? "(filtered)" : ""}</span>
            <div className="flex items-center gap-1.5">
              <button type="button" data-testid="explorer-prev" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1.5 rounded border border-primary/15 text-muted-foreground hover:text-primary disabled:opacity-30"><CaretLeft size={13} /></button>
              <span className="text-[11px] mono text-foreground px-2">{page}</span>
              <button type="button" data-testid="explorer-next" disabled={rawItems.length < f.count || loading} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded border border-primary/15 text-muted-foreground hover:text-primary disabled:opacity-30"><CaretRight size={13} /></button>
            </div>
          </div>
        </section>

        <DetailPanel item={selected} onPickAsset={onPickAsset} />
      </div>
    </main>
  );
};

export const AssetExplorerPage = ({ onPickAsset }) => (
  <TokenMetaProvider><ExplorerInner onPickAsset={onPickAsset} /></TokenMetaProvider>
);
